import { getTrndsClientSongTiktokStats, TrndsClientSongTiktokStats } from '@round/api';
import { useCallback, useState } from 'react';
import useAbortableEffect from '../../../../Hooks/useAbortableEffect';

type UseClientSongsParams = Parameters<typeof getTrndsClientSongTiktokStats>[1] & { clientId: number };

export default function useClientSongs({ clientId, page, search, page_size, ordering }: UseClientSongsParams) {
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasErrorLoading, setHasErrorLoading] = useState(false);

    const [songStats, setSongStats] = useState<TrndsClientSongTiktokStats[]>([]);
    const [count, setCount] = useState(0);

    const fetchData = useCallback(
        async (requestInit?: RequestInit) => {
            try {
                setIsLoading(true);
                setHasErrorLoading(false);
                const response = await getTrndsClientSongTiktokStats(
                    clientId,
                    { search, page_size, ordering, page },
                    requestInit
                );
                if (response.status !== 200) {
                    setHasErrorLoading(true);
                    return;
                }

                setSongStats(response.data.results);
                setCount(response.data.count);
                setIsInitialized(true);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setHasErrorLoading(true);
            } finally {
                setIsLoading(false);
            }
        },
        [clientId, ordering, page, page_size, search]
    );

    useAbortableEffect(
        (signal) => {
            fetchData({ signal });
        },
        [fetchData]
    );

    return {
        songStats,
        count,
        isLoading,
        hasErrorLoading,
        isInitialized,
        reset: () => {
            setSongStats([]);
            setCount(0);
            setIsLoading(false);
            setHasErrorLoading(false);
            setIsInitialized(false);
        },
        refresh: fetchData,
    };
}
